import React from "react";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(() => ({
    "@keyframes fadeIn": {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
    "@keyframes splatter": {
        from: { opacity: 0, transform: "scale(0)" },
        to: { opacity: 1, transform: "scale(1)" },
    },
    background: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: -1,
    },
    shape: {
        position: "absolute",
        borderRadius: "50%",
        animationName: "$fadeIn",
        animationTimingFunction: "ease-in",
    },
    splatterShape: {
        position: "absolute",
        borderRadius: "50%",
        animationName: "$splatter",
        animationTimingFunction: "ease-out",
    },
}));

const generateRandomShapes = (numShapes) => {
    const colors = [
        "rgba(255, 182, 193, 0.5)", // Light Pink
        "rgba(173, 216, 230, 0.5)", // Light Blue
        "rgba(144, 238, 144, 0.5)", // Light Green
        "rgba(255, 255, 224, 0.5)", // Light Yellow
        "rgba(255, 228, 196, 0.5)", // Light Peach
    ];

    const shapes = [];
    for (let i = 0; i < numShapes; i++) {
        const size = Math.random() * 200 + 100; // Random size between 100 and 300
        const top = Math.random() * 100; // Random top position between 0% and 100%
        const left = Math.random() * 100; // Random left position between 0% and 100%
        const color = colors[Math.floor(Math.random() * colors.length)]; // Random color
        const opacity = Math.random() * 0.5 + 0.3; // Random opacity between 0.3 and 0.8
        const duration = Math.random() * 2 + 1; // Random duration between 1s and 3s

        shapes.push({
            width: size,
            height: size,
            top: `${top}%`,
            left: `${left}%`,
            backgroundColor: color,
            initialOpacity: 0, // Initial opacity set to 0
            finalOpacity: opacity,
            animationDuration: `${duration}s`,
        });
    }

    return shapes;
};

const generateSplatterShapes = (numShapes) => {
    const colors = [
        "rgba(255, 99, 71, 0.7)", // Tomato
        "rgba(30, 144, 255, 0.7)", // Dodger Blue
        "rgba(50, 205, 50, 0.7)", // Lime Green
        "rgba(255, 215, 0, 0.7)", // Gold
        "rgba(255, 105, 180, 0.7)", // Hot Pink
    ];

    const chosenColor = colors[Math.floor(Math.random() * colors.length)]; // Choose one color from the array

    const startPos = { top: Math.random() * 100, left: 0 }; // Random start position on the left edge
    const endPos = { top: Math.random() * 100, left: 100 }; // Random end position on the right edge

    const controlPoint1 = { top: Math.random() * 100, left: 50 }; // Control point for Bezier curve
    const controlPoint2 = { top: Math.random() * 100, left: 50 }; // Control point for Bezier curve

    const shapes = [];
    for (let i = 0; i < numShapes; i++) {
        const size = Math.random() * 20 + 10; // Random size between 10 and 30
        const t = i / numShapes; // Parameter t for interpolation
        const top = (1 - t) * (1 - t) * (1 - t) * startPos.top + 3 * (1 - t) * (1 - t) * t * controlPoint1.top + 3 * (1 - t) * t * t * controlPoint2.top + t * t * t * endPos.top + Math.random() * 10 - 5; // Cubic Bezier curve with randomness
        const left = (1 - t) * (1 - t) * (1 - t) * startPos.left + 3 * (1 - t) * (1 - t) * t * controlPoint1.left + 3 * (1 - t) * t * t * controlPoint2.left + t * t * t * endPos.left + Math.random() * 10 - 5; // Cubic Bezier curve with randomness
        const opacity = Math.random() * 0.5 + 0.5; // Random opacity between 0.5 and 1
        const duration = Math.random() * 1 + 0.5; // Random duration between 0.5s and 1.5s

        shapes.push({
            width: size,
            height: size,
            top: `${top}%`,
            left: `${left}%`,
            backgroundColor: chosenColor,
            finalOpacity: opacity,
            animationDuration: `${duration}s`,
        });
    }

    return shapes;
};

const generateSplatterShapes2 = (numShapes) => {
    const colors = [
        "rgba(255, 69, 0, 0.7)", // Red Orange
        "rgba(0, 191, 255, 0.7)", // Deep Sky Blue
        "rgba(34, 139, 34, 0.7)", // Forest Green
        "rgba(255, 140, 0, 0.7)", // Dark Orange
        "rgba(255, 20, 147, 0.7)", // Deep Pink
    ];

    const chosenColor = colors[Math.floor(Math.random() * colors.length)]; // Choose one color from the array

    const startPos = { top: Math.random() * 100, left: 100 }; // Random start position on the right edge
    const endPos = { top: Math.random() * 100, left: 0 }; // Random end position on the left edge

    const controlPoint1 = { top: Math.random() * 100, left: 50 }; // Control point for Bezier curve
    const controlPoint2 = { top: Math.random() * 100, left: 50 }; // Control point for Bezier curve

    const shapes = [];
    for (let i = 0; i < numShapes; i++) {
        const size = Math.random() * 20 + 10; // Random size between 10 and 30
        const t = i / numShapes; // Parameter t for interpolation
        const top = (1 - t) * (1 - t) * (1 - t) * startPos.top + 3 * (1 - t) * (1 - t) * t * controlPoint1.top + 3 * (1 - t) * t * t * controlPoint2.top + t * t * t * endPos.top + Math.random() * 10 - 5; // Cubic Bezier curve with randomness
        const left = (1 - t) * (1 - t) * (1 - t) * startPos.left + 3 * (1 - t) * (1 - t) * t * controlPoint1.left + 3 * (1 - t) * t * t * controlPoint2.left + t * t * t * endPos.left + Math.random() * 10 - 5; // Cubic Bezier curve with randomness
        const opacity = Math.random() * 0.5 + 0.5; // Random opacity between 0.5 and 1
        const duration = Math.random() * 1 + 0.5; // Random duration between 0.5s and 1.5s

        shapes.push({
            width: size,
            height: size,
            top: `${top}%`,
            left: `${left}%`,
            backgroundColor: chosenColor,
            finalOpacity: opacity,
            animationDuration: `${duration}s`,
        });
    }

    return shapes;
};

const StaticBackground = ({ numShapes = 12, numSplatterShapes = 50 }) => {
    const classes = useStyles();
    const shapes = generateRandomShapes(numShapes); // Generate random shapes based on numShapes
    const splatterShapes = generateSplatterShapes(numSplatterShapes); // Generate random splatter shapes
    const splatterShapes2 = generateSplatterShapes2(numSplatterShapes); // Generate second set of random splatter shapes

    return (
        <Box className={classes.background}>
            {shapes.map((shape, index) => (
                <Box
                    key={index}
                    className={classes.shape}
                    sx={{
                        width: shape.width,
                        height: shape.height,
                        top: shape.top,
                        left: shape.left,
                        backgroundColor: shape.backgroundColor,
                        opacity: shape.initialOpacity,
                        animationDuration: shape.animationDuration,
                        animationFillMode: "forwards", // Ensure the animation stays at the final state
                        animationName: "$fadeIn",
                        animationTimingFunction: "ease-in",
                        animationDelay: `${Math.random() * 2}s`, // Random delay for each shape
                    }}
                />
            ))}
            {splatterShapes.map((shape, index) => (
                <Box
                    key={index}
                    className={classes.splatterShape}
                    sx={{
                        width: shape.width,
                        height: shape.height,
                        top: shape.top,
                        left: shape.left,
                        backgroundColor: shape.backgroundColor,
                        opacity: 0,
                        animationDuration: shape.animationDuration,
                        animationFillMode: "forwards", // Ensure the animation stays at the final state
                        animationName: "$splatter",
                        animationTimingFunction: "ease-out",
                        animationDelay: `${Math.random() * 1}s`, // Random delay for each shape
                    }}
                />
            ))}
            {splatterShapes2.map((shape, index) => (
                <Box
                    key={index}
                    className={classes.splatterShape}
                    sx={{
                        width: shape.width,
                        height: shape.height,
                        top: shape.top,
                        left: shape.left,
                        backgroundColor: shape.backgroundColor,
                        opacity: 0,
                        animationDuration: shape.animationDuration,
                        animationFillMode: "forwards", // Ensure the animation stays at the final state
                        animationName: "$splatter",
                        animationTimingFunction: "ease-out",
                        animationDelay: `${Math.random() * 1}s`, // Random delay for each shape
                    }}
                />
            ))}
        </Box>
    );
};

export default StaticBackground;